import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { supplierAPI } from "../../services/api";
import { useNotification } from "../../hooks/useNotification";

const SupplierList = () => {
  const { t, i18n } = useTranslation();
  const { showError } = useNotification();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const isRTL = i18n.language === "ar";

  useEffect(() => {
    loadSuppliers();
  }, [search]);

  const loadSuppliers = async () => {
    try {
      const response = await supplierAPI.getAll({ search });
      setSuppliers(response.data.data);
    } catch (error) {
      showError(t("messages.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("messages.confirmDelete"))) return;
    try {
      await supplierAPI.delete(id);
      loadSuppliers();
    } catch (error) {
      showError(t("messages.error"));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t("suppliers.title")}
          </h2>
          <p className="text-gray-600 mt-1">{t("suppliers.subtitle")}</p>
        </div>
        <Link
          to="/suppliers/new"
          className="flex items-center px-4 py-2 bg-gradient-to-r bg-gray-900 text-white rounded-lg hover:bg-yellow-500 transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          <svg
            className="h-5 w-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          {t("suppliers.addSupplier")}
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="mb-6">
          <div className="relative">
            <input
              type="text"
              placeholder={t("common.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <svg
              className="absolute left-3 top-3.5 h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* Modern Card Layout */}
        <div className="space-y-3">
          {/* Header */}
          <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 rounded-lg text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="col-span-3">{t("suppliers.name")}</div>
            <div className="col-span-2">{t("suppliers.contactPerson")}</div>
            <div className="col-span-3">{t("suppliers.email")}</div>
            <div className="col-span-2">{t("suppliers.phone")}</div>
            <div className="col-span-1">{t("common.status")}</div>
            <div className="col-span-1 text-right">{t("common.actions")}</div>
          </div>

          {/* Card Items */}
          {suppliers.map((supplier) => (
            <div
              key={supplier.id}
              className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100"
            >
              <div className="grid grid-cols-12 gap-4 px-6 py-4 items-center">
                {/* Supplier Name */}
                <div className="col-span-3">
                  <div className="text-sm font-semibold text-gray-900">
                    {supplier.name}
                  </div>
                </div>

                {/* Contact Person */}
                <div className="col-span-2">
                  <div className="text-sm text-gray-600">
                    {supplier.contact_person || "-"}
                  </div>
                </div>

                {/* Email */}
                <div className="col-span-3">
                  <div className="text-sm text-gray-600">
                    {supplier.email || "-"}
                  </div>
                </div>

                {/* Phone */}
                <div className="col-span-2">
                  <div className="text-sm text-gray-600">
                    {supplier.phone || "-"}
                  </div>
                </div>

                {/* Status */}
                <div className="col-span-1 flex items-center gap-2">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      supplier.is_active ? "bg-green-500" : "bg-red-500"
                    }`}
                  ></div>
                  <span className="text-xs font-medium text-gray-900">
                    {supplier.is_active
                      ? t("users.status.active")
                      : t("users.status.inactive")}
                  </span>
                </div>

                {/* Actions */}
                <div className="col-span-1 flex items-center justify-end gap-2">
                  <Link
                    to={`/suppliers/${supplier.id}`}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title={t("common.edit")}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </Link>
                  <button
                    onClick={() => handleDelete(supplier.id)}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title={t("common.delete")}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {suppliers.length === 0 && (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            <p className="mt-4 text-sm text-gray-600">
              {t("suppliers.noSuppliers")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierList;
